/**
 * mod2notifications.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Self-contained notification module for the medication tracker.
 * 
 * WHAT THIS MODULE DOES:
 *  1. Requests notification permission from the user
 *  2. Gets an FCM push token and saves it to Firestore
 *  3. Refreshes the token silently on every app load (handles PWA reinstalls)
 *  4. Handles foreground messages (app is open) via onMessage()
 *  5. Exposes scheduleNotificationsForMed() — call this every time a medication
 *     is "taken". It writes the next 3 scheduled push times (30 min before,
 *     10 min before, and exact dose time) onto the Firestore medication doc.
 *     The Cloud Function (functions/index.js) reads those and fires the pushes.
 * 
 * ANDROID NOTES:
 *  - All messages are sent with priority: 'high' so Android wakes the device
 *  - channelId: 'medication_reminders' must exist on the device (created by
 *    the Firebase SDK automatically for web push; for native Android you'd
 *    create it in MainActivity, but for PWA this is handled)
 *  - The service worker (firebase-messaging-sw.js) handles background display
 *
 * USAGE:
 *  // In App.js, after auth is ready:
 *  import { initNotifications } from './services/mod2notifications';
 *  await initNotifications(currentUser);
 *
 *  // After user takes a medication:
 *  import { scheduleNotificationsForMed } from './services/mod2notifications';
 *  await scheduleNotificationsForMed(medDocId, medName, prescription, nextDoseDate);
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db, auth } from '../firebase';

// ─── Constants ───────────────────────────────────────────────────────────────

const VAPID_KEY =
  'BCuFDqsvDAXHX4KhojSuZza3VNA0qF4s3i4FwvM2oN9Whqu0LJeylqz-_3IXVhtqEQLQFlRnOuy6cYzPuaRPfaA';

const MEDICATIONS_COLLECTION = 'meds2';

// Fixed reminder offsets — not configurable, always 30 min and 10 min before
const REMINDER_OFFSETS_MS = [
  { type: '30min', label: '30 minutes',  offsetMs: 30 * 60 * 1000 },
  { type: '10min', label: '10 minutes',  offsetMs: 10 * 60 * 1000 },
  { type: 'exact', label: 'right now',   offsetMs: 0               }
];

// ─── Internal state ──────────────────────────────────────────────────────────

let _messagingInstance = null;
let _foregroundUnsubscribe = null;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Returns the singleton Firebase Messaging instance.
 * Throws a clear error if messaging isn't supported (e.g. Safari < 16.4).
 */
function getMessagingInstance() {
  if (_messagingInstance) return _messagingInstance;

  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    throw new Error('Service workers not supported in this environment');
  }

  try {
    // getMessaging() is idempotent for the same app, so this is safe
    _messagingInstance = getMessaging();
    return _messagingInstance;
  } catch (err) {
    throw new Error(`Firebase Messaging init failed: ${err.message}`);
  }
}

/**
 * Waits for the service worker to be ready with a timeout.
 * Returns the registration or null if it times out.
 */
async function getServiceWorkerRegistration(timeoutMs = 4000) {
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('SW ready timeout')), timeoutMs)
      )
    ]);
  } catch (err) {
    console.warn('[mod2notifications] Service worker not ready:', err.message);
    return null;
  }
}

/**
 * Saves the FCM token to:
 *  - users/{uid}.fcmToken  (for support notifications + backup)
 *  - All meds2 docs belonging to this user (Cloud Function reads fcmToken from med doc)
 */
async function saveTokenToFirestore(uid, token) {
  if (!uid || !token) return;

  try {
    // 1. Save to user doc
    await updateDoc(doc(db, 'users', uid), { fcmToken: token });
    console.log('[mod2notifications] Token saved to users doc');

    // 2. Save to ALL existing medication docs for this user
    //    The Cloud Function reads fcmToken directly from the med doc,
    //    so if the token rotates, old scheduled notifications still fire correctly.
    const medsQuery = query(
      collection(db, MEDICATIONS_COLLECTION),
      where('userId', '==', uid)
    );
    const medsSnap = await getDocs(medsQuery);

    const updates = medsSnap.docs.map(medDoc =>
      updateDoc(medDoc.ref, { fcmToken: token })
    );
    await Promise.all(updates);

    if (medsSnap.docs.length > 0) {
      console.log(`[mod2notifications] Token propagated to ${medsSnap.docs.length} medication doc(s)`);
    }
  } catch (err) {
    // Non-fatal: token may already be set or user doc may not exist yet
    console.warn('[mod2notifications] Could not save token to Firestore:', err.message);
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * initNotifications(user)
 * ─────────────────────────────────────────────────────────────────────────────
 * Call this once when the app loads and the user is authenticated.
 * It will:
 *   - Request notification permission if not yet granted
 *   - Get (or refresh) the FCM token
 *   - Save the token to Firestore
 *   - Set up the foreground message handler
 *
 * Returns: { granted: boolean, token: string|null, error: string|null }
 */
export async function initNotifications(user) {
  // Guard: needs a logged-in user
  if (!user?.uid) {
    return { granted: false, token: null, error: 'No authenticated user' };
  }

  // Guard: browser support check
  if (!('Notification' in window)) {
    return { granted: false, token: null, error: 'Notifications not supported' };
  }

  // Guard: service worker support
  if (!('serviceWorker' in navigator)) {
    return { granted: false, token: null, error: 'Service workers not supported' };
  }

  let messaging;
  try {
    messaging = getMessagingInstance();
  } catch (err) {
    return { granted: false, token: null, error: err.message };
  }

  // ── Step 1: Request permission ──────────────────────────────────────────────
  let permission = Notification.permission;

  if (permission === 'default') {
    try {
      permission = await Promise.race([
        Notification.requestPermission(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Permission timeout')), 6000)
        )
      ]);
    } catch {
      permission = 'default';
    }
  }

  if (permission !== 'granted') {
    console.warn('[mod2notifications] Permission not granted:', permission);
    return { granted: false, token: null, error: `Permission ${permission}` };
  }

  // ── Step 2: Get FCM token ───────────────────────────────────────────────────
  let token = null;
  try {
    const registration = await getServiceWorkerRegistration();

    token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      ...(registration ? { serviceWorkerRegistration: registration } : {})
    });

    if (!token) {
      console.warn('[mod2notifications] getToken returned empty — retry on next load');
      return { granted: true, token: null, error: 'Token unavailable, try again' };
    }

    console.log('[mod2notifications] FCM token:', token.substring(0, 20) + '…');
  } catch (err) {
    console.error('[mod2notifications] getToken error:', err);
    return { granted: true, token: null, error: err.message };
  }

  // ── Step 3: Persist token ───────────────────────────────────────────────────
  await saveTokenToFirestore(user.uid, token);

  // ── Step 4: Foreground message handler ─────────────────────────────────────
  // Tear down any existing listener before creating a new one
  if (_foregroundUnsubscribe) {
    _foregroundUnsubscribe();
    _foregroundUnsubscribe = null;
  }

  _foregroundUnsubscribe = onMessage(messaging, (payload) => {
    console.log('[mod2notifications] Foreground message:', payload);
    _showForegroundNotification(payload);
  });

  console.log('[mod2notifications] Initialized successfully ✓');
  return { granted: true, token, error: null };
}

/**
 * _showForegroundNotification(payload)
 * Shows a notification via the service worker when the app is in the foreground.
 * Using SW.showNotification (instead of new Notification()) means action buttons
 * like "Take Now" and "Snooze" are available, and Android renders it correctly.
 */
async function _showForegroundNotification(payload) {
  if (Notification.permission !== 'granted') return;

  const title = payload.notification?.title || '💊 Medication Reminder';
  const body  = payload.notification?.body  || 'Time to take your medication';
  const medId = payload.data?.medicationId  || '';
  const type  = payload.data?.type          || 'exact';

  const options = {
    body,
    icon:             '/pill192.png',
    badge:            '/pillbadge.png',
    tag:              `med-reminder-${medId}-${type}`,
    renotify:         true,
    requireInteraction: true,          // keeps notification visible on Android
    vibrate:          [200, 100, 200, 100, 200],
    data: {
      url:          payload.data?.url || '/trackmed2',
      medicationId: medId,
      type
    },
    actions: [
      { action: 'take',   title: '✓ Take Now'      },
      { action: 'snooze', title: '⏰ Snooze 10 min' }
    ]
  };

  try {
    const registration = await getServiceWorkerRegistration();
    if (registration) {
      await registration.showNotification(title, options);
    } else {
      // Fallback: basic Notification API (no action buttons, but better than nothing)
      new Notification(title, { body, icon: '/pill192.png' });
    }
  } catch (err) {
    console.error('[mod2notifications] Could not show foreground notification:', err);
  }
}

/**
 * scheduleNotificationsForMed(medDocId, medName, prescription, nextDoseDate, fcmToken?)
 * ─────────────────────────────────────────────────────────────────────────────
 * Call this every time the user logs a "Take Now" or "Log Time" action.
 * Writes `scheduledNotifications` to the Firestore medication document so the
 * Cloud Function (sendMedicationReminders) can fire the pushes at the right time.
 *
 * Reminders are FIXED at:
 *   • 30 minutes before next dose
 *   • 10 minutes before next dose
 *   • At exact next dose time
 *
 * All sendAt values are stored as UTC ISO strings (e.g. "2024-12-17T11:30:00.000Z")
 * so the Cloud Function (which runs in UTC) parses them correctly regardless of
 * what timezone the user's device is in.
 *
 * @param {string}  medDocId     - Firestore document ID in meds2 collection
 * @param {string}  medName      - Human-readable medication name (e.g. "Amoxicillin")
 * @param {string}  prescription - e.g. "1x2"
 * @param {Date}    nextDoseDate - JS Date object representing when the next dose is due
 * @param {string?} fcmToken     - Optional: pass current token to ensure it's up to date
 */
export async function scheduleNotificationsForMed(
  medDocId,
  medName,
  prescription,
  nextDoseDate,
  fcmToken = null
) {
  // ── Validate inputs ─────────────────────────────────────────────────────────
  if (!medDocId) {
    console.error('[mod2notifications] scheduleNotificationsForMed: missing medDocId');
    return { success: false, error: 'Missing medDocId' };
  }

  if (!(nextDoseDate instanceof Date) || isNaN(nextDoseDate.getTime())) {
    console.error('[mod2notifications] scheduleNotificationsForMed: invalid nextDoseDate', nextDoseDate);
    return { success: false, error: 'Invalid nextDoseDate' };
  }

  const now = new Date();
  if (nextDoseDate <= now) {
    // Next dose is already in the past — nothing to schedule
    console.warn('[mod2notifications] nextDoseDate is in the past, skipping schedule');
    return { success: false, error: 'nextDoseDate is in the past' };
  }

  // ── Build the scheduled notifications array ─────────────────────────────────
  // Each entry: { type, sendAt (UTC ISO string), sent, medicationName, prescription }
  const scheduledNotifications = [];

  for (const { type, label, offsetMs } of REMINDER_OFFSETS_MS) {
    const sendAt = new Date(nextDoseDate.getTime() - offsetMs);

    // Only schedule reminders that are still in the future
    // (e.g. if next dose is only 5 minutes away, skip the 10-min and 30-min reminders)
    if (sendAt <= now) {
      console.log(`[mod2notifications] Skipping ${type} reminder (${label}) — sendAt is in the past`);
      continue;
    }

    scheduledNotifications.push({
      type,
      sendAt:          sendAt.toISOString(), // ← ALWAYS UTC ISO string with Z suffix
      sent:            false,
      medicationName:  medName        || 'Medication',
      prescription:    prescription   || '',
      scheduledAt:     now.toISOString()
    });

    console.log(
      `[mod2notifications] Scheduled ${type} reminder for "${medName}" at ${sendAt.toISOString()}`
    );
  }

  if (scheduledNotifications.length === 0) {
    console.warn('[mod2notifications] No future reminders to schedule for', medName);
    return { success: true, scheduled: 0, note: 'All reminders were in the past' };
  }

  // ── Get the current FCM token ───────────────────────────────────────────────
  // We write it on the med doc so the Cloud Function doesn't need a second read
  let tokenToSave = fcmToken;
  if (!tokenToSave) {
    try {
      const uid = auth.currentUser?.uid;
      if (uid) {
        const userSnap = await getDoc(doc(db, 'users', uid));
        tokenToSave = userSnap.data()?.fcmToken ?? null;
      }
    } catch (err) {
      console.warn('[mod2notifications] Could not fetch token from user doc:', err.message);
    }
  }

  // ── Write to Firestore ──────────────────────────────────────────────────────
  try {
    const medRef = doc(db, MEDICATIONS_COLLECTION, medDocId);
    const updatePayload = {
      scheduledNotifications,
      nextDose:             nextDoseDate.toISOString(), // also keep nextDose in sync
      reminderSent:         false,
      lastReminderSentAt:   null,
      notificationsUpdatedAt: now.toISOString()
    };

    // Only update fcmToken on the med doc if we have one
    if (tokenToSave) {
      updatePayload.fcmToken = tokenToSave;
    }

    await updateDoc(medRef, updatePayload);

    console.log(
      `[mod2notifications] ✓ Wrote ${scheduledNotifications.length} notification(s) to Firestore for "${medName}"`
    );

    return {
      success:   true,
      scheduled: scheduledNotifications.length,
      reminders: scheduledNotifications.map(n => ({ type: n.type, sendAt: n.sendAt }))
    };
  } catch (err) {
    console.error('[mod2notifications] Failed to write scheduledNotifications:', err);
    return { success: false, error: err.message };
  }
}

/**
 * teardownNotifications()
 * Call this if the user logs out — removes the foreground listener.
 */
export function teardownNotifications() {
  if (_foregroundUnsubscribe) {
    _foregroundUnsubscribe();
    _foregroundUnsubscribe = null;
    console.log('[mod2notifications] Foreground listener removed');
  }
}

/**
 * getNotificationPermissionStatus()
 * Convenience helper to check current permission state.
 * Returns 'granted' | 'denied' | 'default' | 'unsupported'
 */
export function getNotificationPermissionStatus() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}